import { Badge, Box, Stack, useMantineTheme } from '@mantine/core';
import { format, parseISO } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';

import { formatDollars } from '~/domains/common/utils/formatters';
import { EmptyState } from '~/domains/design-system/components/EmptyState';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import type {
  CryptoDepositArrival,
  CryptoDepositArrivalStatus,
} from '~/services/wallet/interfaces/crypto-direct.interface';
import dt from '~/testing';

import type { CryptoArrivalsStatus } from '../../hooks/useCryptoDepositArrivals';

import { getNetworkLabel } from './constants';

const STATUS_BADGE_COLOR = {
  pending: 'blue',
  under_review: 'yellow',
  completed: 'teal',
  returned: 'red',
} as const satisfies Record<CryptoDepositArrivalStatus, string>;

// `observedAmount` is already dollars (CAG `toDollars`); format only, never divide.
const formatObservedAmount = (dollars: number) => formatDollars(dollars, true);

type CryptoArrivalsListProps = {
  arrivals: CryptoDepositArrival[];
  /**
   * The list's render state, from the arrivals hook. Required (not defaulted) so no caller can
   * silently assert "ready" and surface a false empty state. 'loading' → blank content (no spinner);
   * 'error' → blank content (never the "no transfers" claim — the fetch failed, we don't know);
   * 'ready' → rows, or the design-system EmptyState when genuinely empty.
   */
  status: CryptoArrivalsStatus;
};

export function CryptoArrivalsList({ arrivals, status }: CryptoArrivalsListProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();
  const hasUnderReview = arrivals.some((item) => item.status === 'under_review');

  // Content area, and NONE of the pre-'ready' states is a spinner. Crucially, 'error' must NOT fall
  // through to the empty copy — a failed fetch is not a confirmed "no transfers" (EDGE-31129, panel).
  // Mark's ruling: on a first-fetch error, render nothing (no error copy). The state is self-healing —
  // the next successful 30s poll flips status back to 'ready'.
  const renderContent = () => {
    if (status === 'loading' || status === 'error') return null;

    if (arrivals.length === 0) {
      return (
        // Compact, low-emphasis empty state: it sits INSIDE the "Recent transfers" section, so its
        // title must not out-weigh that section's `subhead-medium` (14/600) heading. The compound
        // Title defaults to `headline-xxl` (24/900) — far too heavy here — so we override it to body
        // weight (`body-large` 14/400) and the body line to `body-medium`, and halve the compound's
        // default 48px vertical padding to fit the compact deposit column. (Mark's "lighten it" ruling,
        // EDGE-32112/32113.) Same test-id + copy keys → the DOM the tests assert is unchanged.
        <EmptyState
          data-test-id={dt.payments.deposits.cryptoDirect.arrivalsEmpty}
          py={theme.other.spacing._24}
        >
          <EmptyState.Title variant="body-large">
            {t('deposits.cryptoDirect.arrivals.emptyTitle')}
          </EmptyState.Title>
          <EmptyState.Text variant="body-medium">
            {t('deposits.cryptoDirect.arrivals.emptyBody')}
          </EmptyState.Text>
        </EmptyState>
      );
    }

    return (
      // Mantine 5 Stack/Group are not polymorphic — Box carries the semantic list tags.
      <Box
        component="ul"
        role="list"
        p={0}
        m={0}
        sx={{
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.other.spacing._8,
        }}
        data-test-id={dt.payments.deposits.cryptoDirect.arrivalsList}
      >
        {arrivals.map((item) => (
          <Box
            component="li"
            key={item.id}
            p={theme.other.spacing._12}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: theme.other.spacing._8,
              border: `1px solid ${theme.colors.gray2[1]}`,
              borderRadius: theme.radius.md,
            }}
            data-test-id={dt.payments.deposits.cryptoDirect.arrivalItem}
          >
            <Stack spacing={theme.other.spacing._2}>
              <Text variant="subhead-medium">{formatObservedAmount(item.observedAmount)}</Text>
              <Text variant="body-medium" c="gray2.6">
                {[
                  item.chain ? getNetworkLabel(item.chain) : null,
                  format(parseISO(item.postedAt), 'MMM d, h:mm a'),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            </Stack>
            <Badge color={STATUS_BADGE_COLOR[item.status]} variant="light" size="lg">
              {t(`deposits.cryptoDirect.arrivals.status.${item.status}`)}
            </Badge>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Stack spacing={theme.other.spacing._12} w="100%">
      <Title tag="h3" variant="subhead-medium">
        {t('deposits.cryptoDirect.arrivals.title')}
      </Title>

      {renderContent()}

      {hasUnderReview && (
        <Text variant="body-medium" c="gray2.6">
          {t('deposits.cryptoDirect.arrivals.underReviewHint')}
        </Text>
      )}
    </Stack>
  );
}
